import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reservePlanEnded } from '@/lib/reservePlanEnded';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlaneTakeoff,
  ChevronDown,
  Calendar,
  CreditCard,
  Clock,
  Shield,
  ArrowRight,
  AlertTriangle,
  X,
  RefreshCw,
  Loader2,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Search,
  Ticket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders } from '@/lib/reserveActAs';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import { toast } from 'sonner';
import SectionBand from '@/components/ui/SectionBand';
import { getMarketingFareName } from '@/utils/marketingFareName';
import { fmtCurrency } from '@/utils/money';
import { writePassengerParams } from '@/utils/passengerTypes';

// In dev, call API on 3005 directly so inject never hits proxy
const API_ORIGIN = import.meta.env.DEV ? 'http://localhost:3005' : '';
const frequencyLabels = { weekly: 'Weekly', biweekly: 'Every 2 weeks', monthly: 'Monthly' };
const statusColors = {
  active: 'bg-emerald-500/10 text-emerald-400',
  paused: 'bg-brand-500/10 text-brand-400',
  completed: 'bg-brand-500/10 text-brand-400',
  cancelled: 'bg-red-500/10 text-red-400',
  ticketed: 'bg-brand-500/10 text-brand-400'
};
const statusLabels = {
  active: 'active',
  paused: 'paused',
  completed: 'Ready for Ticketing',
  cancelled: 'cancelled',
  ticketed: 'ticketed'
};

function formatDuration(isoDuration) {
  if (!isoDuration) return '';
  const matches = isoDuration.match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return '';
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return `${hours}h ${minutes}m`;
}

function formatTime(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'h:mm a');
}

function formatDate(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'EEE, MMM d');
}

function formatCabin(str) {
  if (!str) return null;
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

function CancelConfirmOverlay({ trip, onConfirm, onClose, isLoading }) {
  const paidAmount = trip.paid_amount_cents || 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 shadow-2xl"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-white text-lg font-medium mb-1">Cancel Reserve Plan?</h3>
          <p className="text-white/65 text-sm font-normal mb-5 leading-relaxed">
            Future payments will stop immediately. Credits already paid are non-withdrawable and remain in your Reserve balance.
          </p>

          {paidAmount > 0 && (
            <div className="w-full rounded-xl bg-white/[0.03] border border-white/5 p-3 mb-5">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-white/30">Paid so far</span>
                <span className="text-white/50">${fmtCurrency(paidAmount)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/30">Stays in balance</span>
                <span className="text-emerald-400/70">${fmtCurrency(paidAmount)}</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 w-full">
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 border border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-full text-xs h-10"
            >
              Keep Plan
            </Button>
            <Button
              size="sm"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/20 rounded-full text-xs h-10"
            >
              {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Yes, Cancel Plan'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SliceCard({ slice, label, cabinFareName }) {
  const segment = slice.segments?.[0];
  const lastSegment = slice.segments?.[slice.segments.length - 1];
  if (!segment) return null;
  const marketingCarrier = segment.marketing_carrier;
  // Name-first, same as TripCard's header logo below.
  const carrierLogo = resolveCarrierLogo(marketingCarrier);
  const stops = (slice.segments?.length || 1) - 1;
  const aircraft = segment.aircraft?.name;
  const flightNumber = segment.marketing_carrier_flight_number;
  // I74BF5: prefer the trip-level fare brand (resolved by getMarketingFareName
  // upstream so it sees the customer's bookingOption pick — "Level",
  // "Polaris Business", etc.) over segment-level marketing names, which are
  // routinely empty on SerpAPI-synthesized Reserve offers and were the reason
  // this row used to render blank. Segment fallback stays in place for legacy
  // Duffel offers that have the segment fields populated but no bookingOption.
  const cabinDisplay = cabinFareName ||
                       segment.passengers_cabin_class_marketing_name ||
                       segment.cabin_class_marketing_name ||
                       segment.cabin_class;

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 sm:p-5">
      <div className="flex items-center justify-between mb-2 sm:mb-3 pb-1.5 sm:pb-2 border-b border-white/[0.04]">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/30" />
          <span className="text-white/50 text-xs sm:text-xs font-normal uppercase tracking-wide">
            {label} • {formatDate(segment.departing_at)}
          </span>
        </div>
        {stops > 0 && (
          <span className="text-brand-400/80 text-[10px] sm:text-[10px] uppercase tracking-wide font-normal bg-brand-400/[0.08] px-1.5 sm:px-2 py-0.5 rounded">
            {stops} stop{stops > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 sm:gap-4">
          <div className="flex items-center justify-center mt-0.5 flex-shrink-0">
            {carrierLogo ? (
              <img
                src={carrierLogo}
                alt={marketingCarrier?.name}
                className="w-7 h-7 sm:w-9 sm:h-9 object-contain"
              />
            ) : (
              <PlaneTakeoff className="w-5 h-5 sm:w-6 sm:h-6 text-white/30" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white/90 text-xs sm:text-base font-medium whitespace-nowrap">
              {formatTime(segment.departing_at)} – {formatTime(lastSegment?.arriving_at)}
            </p>
            <p className="text-white/30 text-xs sm:text-xs font-normal">
              {marketingCarrier?.name}{flightNumber ? ` ${flightNumber}` : ''}
            </p>
            <p className="text-white/30 text-xs sm:text-xs font-normal">
              {aircraft || 'Aircraft'}{formatCabin(cabinDisplay) ? ` • ${formatCabin(cabinDisplay)}` : ''}
            </p>
            <p className="text-white/30 text-xs sm:text-xs font-normal">
              {stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0 ml-2 sm:ml-4">
          <p className="text-white/30 text-xs sm:text-xs font-normal uppercase tracking-wider mb-0.5">
            {segment.origin?.iata_code}–{lastSegment?.destination?.iata_code}
          </p>
          <p className="text-white/70 text-xs sm:text-sm font-normal">{formatDuration(slice.duration)}</p>
        </div>
      </div>
    </div>
  );
}

function TripCard({ trip, allTrips = [], onRefresh, balanceCents = 0, totalAllocatedCents: totalAllocatedFromProps = 0, allocationColumnMissing = false }) {
  const allocatedFromCurrentTrip = Number(trip?.allocated_from_reserve_cents ?? trip?.allocatedFromReserveCents) || 0;
  const sumFromAllTrips = (allTrips || []).reduce(
    (s, t) => s + (Number(t?.allocated_from_reserve_cents ?? t?.allocatedFromReserveCents) || 0),
    0
  );
  const totalAllocatedCents = Math.max(
    totalAllocatedFromProps || 0,
    sumFromAllTrips,
    allocatedFromCurrentTrip
  );
  const [expanded, setExpanded] = useState(false);
  const [showScheduleChange, setShowScheduleChange] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showInjectModal, setShowInjectModal] = useState(false);
  const [showPushBackModal, setShowPushBackModal] = useState(false);
  const [newFrequency, setNewFrequency] = useState(trip.payment_frequency);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [injectPreview, setInjectPreview] = useState(null);
  const [injectAmountCents, setInjectAmountCents] = useState('');
  const [pushBackAmountCents, setPushBackAmountCents] = useState('');
  const [pushBackPreview, setPushBackPreview] = useState(null);
  const [isLoadingPushBackPreview, setIsLoadingPushBackPreview] = useState(false);
  const [isInjecting, setIsInjecting] = useState(false);
  const [isPushingBack, setIsPushingBack] = useState(false);
  const [isLoadingInjectPreview, setIsLoadingInjectPreview] = useState(false);
  const [injectCap, setInjectCap] = useState(null);
  const [injectCapError, setInjectCapError] = useState(null);
  const [priceCheck, setPriceCheck] = useState(null);
  const [isLoadingPriceCheck, setIsLoadingPriceCheck] = useState(false);
  const [priceCheckError, setPriceCheckError] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  const navigate = useNavigate();

  const handleBookFlight = useCallback(async () => {
    if (!trip?.id || isBooking) return;
    setIsBooking(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to continue');
        return;
      }
      const res = await fetch(reserveUrl('resolve-booking-token'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ trip_id: trip.id })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to load flight options');
        return;
      }
      if (!data.success || !data.booking_token) {
        toast.error('Could not find this flight. It may no longer be available.');
        return;
      }
      navigate('/flightoptions', {
        state: {
          bookingToken: data.booking_token,
          searchParams: data.search_params,
          selectedFlight: data.selectedFlight,
          returnFlight: data.returnFlight ?? null,
          paymentMethod: 'reserve',
          reserveTripId: trip.id
        }
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load flight options');
    } finally {
      setIsBooking(false);
    }
  }, [trip?.id, isBooking, navigate]);

  // Fetch current price when trip card is expanded (for price tracking; active trips only)
  useEffect(() => {
    if (!expanded || !trip?.id || trip?.status !== 'active' || !trip?.flight_data?.slices?.length) return;
    setPriceCheck(null);
    setPriceCheckError(null);
    setIsLoadingPriceCheck(true);
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_ORIGIN}/api/reserve/check-trip-price`, {
          method: 'POST',
          headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` }),
          body: JSON.stringify({ trip_id: trip.id })
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setPriceCheckError(data.error || 'Failed to fetch current price');
          return;
        }
        if (data.success) setPriceCheck(data);
      } catch (e) {
        if (!cancelled) setPriceCheckError(e.message || 'Failed to fetch current price');
      } finally {
        if (!cancelled) setIsLoadingPriceCheck(false);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, trip?.id]);

  // Fetch authoritative max from backend when inject modal opens
  useEffect(() => {
    if (!showInjectModal || !trip?.id) return;
    setInjectCap(null);
    setInjectCapError(null);
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_ORIGIN}/api/reserve/inject-cap?trip_id=${encodeURIComponent(trip.id)}&_=${Date.now()}`, {
          headers: mergeReserveActAsHeaders({ Authorization: `Bearer ${session?.access_token}` }),
          cache: 'no-store'
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setInjectCapError(data.error || 'Failed to load limit');
          return;
        }
        if (data.success && data.max_inject_cents != null) setInjectCap(data);
      } catch (e) {
        if (!cancelled) setInjectCapError(e.message || 'Failed to load limit');
      }
    })();
    return () => { cancelled = true; };
  }, [showInjectModal, trip?.id]);

  const fetchPreview = useCallback(async (freq) => {
    setIsLoadingPreview(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(reserveUrl('preview-schedule'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }),
        body: JSON.stringify({ trip_id: trip.id, payment_frequency: freq })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPreview(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
      setPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [trip.id]);

  const handleOpenScheduleChange = useCallback(async () => {
    setShowScheduleChange(true);
    setNewFrequency(trip.payment_frequency);
    setPreview(null);
  }, [trip.payment_frequency]);

  const handleFrequencySelect = useCallback(async (freq) => {
    setNewFrequency(freq);
    if (freq !== trip.payment_frequency) {
      await fetchPreview(freq);
    } else {
      setPreview(null);
    }
  }, [trip.payment_frequency, fetchPreview]);

  const tripTotalCents = Number(trip?.total_amount_cents ?? trip?.totalAmountCents) || 0;
  const tripPaidCents = Number(trip?.paid_amount_cents ?? trip?.paidAmountCents) || 0;
  const allocatedCents = allocatedFromCurrentTrip;
  const tripRemainingCents = Math.max(0, tripTotalCents - tripPaidCents - allocatedCents);
  // Unallocated = reserve minus all allocations (including this trip's $47)
  const availableToAllocateCents = Math.max(0, (balanceCents || 0) - (totalAllocatedCents ?? 0));
  const maxInjectCentsLocal = Math.min(availableToAllocateCents, tripRemainingCents);
  // Use backend max when present. If backend says total_allocated=0 but this trip has allocation from list, correct it (backend bug workaround).
  const backendTotalAlloc = injectCap?.total_allocated_cents ?? 0;
  const useListAllocFallback = injectCap && backendTotalAlloc === 0 && allocatedCents > 0;
  const effectiveTotalAlloc = useListAllocFallback ? allocatedCents : backendTotalAlloc;
  const effectiveAvailable = injectCap ? Math.max(0, (injectCap.balance_cents ?? 0) - effectiveTotalAlloc) : availableToAllocateCents;
  const effectiveMax = injectCap ? Math.min(effectiveAvailable, injectCap.trip_remaining_cents ?? tripRemainingCents) : maxInjectCentsLocal;
  const maxInjectCents = (injectCap?.max_inject_cents != null && !useListAllocFallback) ? injectCap.max_inject_cents : effectiveMax;
  // For ticketed trips: cap at total to avoid double-counting (booking debit was previously
  // added to allocated, inflating the display). Progress should never exceed 100%.
  const fundedCents = trip.status === 'ticketed'
    ? tripTotalCents
    : tripPaidCents + allocatedCents;
  const progressPercent = tripTotalCents > 0 ? Math.min(100, Math.round((fundedCents / tripTotalCents) * 100)) : 0;
  const showInjectButton = trip.status === 'active' && tripRemainingCents > 0;
  // Past the Deadline the plan's subscription has ended, so switching flights can't rework it.
  const planEnded = reservePlanEnded(trip);
  const deadlineText = trip.deadline_date ? format(parseISO(trip.deadline_date), 'MMM d, yyyy') : '—';

  const firstSlice = trip.flight_data?.slices?.[0];
  const firstSeg = firstSlice?.segments?.[0];
  const originCode = firstSeg?.origin?.iata_code || trip.departure_city || '???';
  const lastSegOfFirstSlice = firstSlice?.segments?.[firstSlice?.segments?.length - 1];
  const destCode = lastSegOfFirstSlice?.destination?.iata_code || trip.arrival_city || '???';
  const airline = firstSeg?.marketing_carrier?.name || trip.marketing_airline || '';
  // Name-first: an admin airline rename must re-brand the logo, not just the label.
  const airlineLogo = resolveCarrierLogo(firstSeg?.marketing_carrier);

  const handleUpdateSchedule = async () => {
    if (newFrequency === trip.payment_frequency) {
      setShowScheduleChange(false);
      return;
    }
    setIsUpdating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(reserveUrl('update-trip-schedule'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({ trip_id: trip.id, payment_frequency: newFrequency })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Payment schedule updated');
      setShowScheduleChange(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || 'Failed to update schedule');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(reserveUrl('cancel-trip'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({ trip_id: trip.id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Reserve plan cancelled. Funds remain in your balance.');
      setShowCancelConfirm(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel');
    } finally {
      setIsCancelling(false);
    }
  };

  const fetchInjectPreview = useCallback(async (amountCentsParam) => {
    const cents = Math.round(Number(amountCentsParam)) || 0;
    if (cents <= 0) {
      setInjectPreview(null);
      return;
    }
    setIsLoadingInjectPreview(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_ORIGIN}/api/reserve/preview-inject`, {
        method: 'POST',
        headers: mergeReserveActAsHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }),
        body: JSON.stringify({ trip_id: trip.id, amount_cents: cents })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setInjectPreview(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
      setInjectPreview(null);
    } finally {
      setIsLoadingInjectPreview(false);
    }
  }, [trip.id]);

  const handleInjectConfirm = async () => {
    const cents = Math.round(parseFloat(injectAmountCents) * 100) || 0;
    if (cents <= 0) {
      toast.error('Enter an amount');
      return;
    }
    setIsInjecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_ORIGIN}/api/reserve/inject-to-trip`, {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({ trip_id: trip.id, amount_cents: cents })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success(data.trip_fully_funded ? 'Trip fully funded from Reserve.' : 'Amount allocated; payment schedule updated.');
      setShowInjectModal(false);
      setInjectAmountCents('');
      setInjectPreview(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || 'Failed to inject');
    } finally {
      setIsInjecting(false);
    }
  };

  const fetchPushBackPreview = async (cents) => {
    if (cents <= 0 || cents > allocatedCents) return;
    setIsLoadingPushBackPreview(true);
    setPushBackPreview(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_ORIGIN}/api/reserve/preview-push-back`, {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`
        }),
        body: JSON.stringify({ trip_id: trip.id, amount_cents: cents })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load preview');
      setPushBackPreview(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
    } finally {
      setIsLoadingPushBackPreview(false);
    }
  };

  const handlePushBackConfirm = async () => {
    const cents = Math.round(parseFloat(pushBackAmountCents) * 100) || 0;
    if (cents <= 0) {
      toast.error('Enter an amount');
      return;
    }
    if (cents > allocatedCents) {
      toast.error('Amount exceeds allocated for this trip');
      return;
    }
    setIsPushingBack(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(reserveUrl('push-back-from-trip'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({ trip_id: trip.id, amount_cents: cents })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Amount returned to general Reserve. Schedule updated.');
      setShowPushBackModal(false);
      setPushBackAmountCents('');
      setPushBackPreview(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || 'Failed to push back');
    } finally {
      setIsPushingBack(false);
    }
  };

  return (
    <>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        {/* Collapsed header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 sm:gap-4 p-3 sm:p-5 hover:bg-white/[0.015] transition-colors text-left"
        >
          <div className="flex-shrink-0 flex items-center justify-center">
            {airlineLogo ? (
              <img src={airlineLogo} alt={airline} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
            ) : (
              <PlaneTakeoff className="w-6 h-6 sm:w-7 sm:h-7 text-white/30" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5">
              <span className="text-white/90 text-xs sm:text-base font-normal whitespace-nowrap">{originCode} → {destCode}</span>
              <span className={`text-[10px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full ${planEnded ? 'bg-white/[0.06] text-white/60 whitespace-nowrap' : statusColors[trip.status]}`}>
                {planEnded ? 'Plan ended' : (statusLabels[trip.status] ?? trip.status)}
              </span>
            </div>
            <p className="text-white/30 text-xs sm:text-xs font-normal truncate">
              {airline} · {trip.departure_date ? format(parseISO(trip.departure_date), 'MMM d, yyyy') : ''}
            </p>
          </div>

          <div className="text-right flex-shrink-0">
            <p className="text-white/85 text-xs sm:text-sm font-normal whitespace-nowrap">${fmtCurrency(fundedCents)} / ${fmtCurrency(tripTotalCents)}</p>
            <div className="w-16 sm:w-24 h-1 bg-white/[0.06] rounded-full mt-1 sm:mt-1.5 overflow-hidden ml-auto">
              <div
                className="h-full bg-emerald-500/80 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/20 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Expanded details */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 sm:px-5 pb-4 sm:pb-5 pt-2 border-t border-white/[0.04] space-y-2.5 sm:space-y-3">
                {/* Flight segment cards */}
                {(() => {
                  // I74BF5: resolve the trip-level fare brand once (uses the
                  // same priority chain as FlightDetailsModal — bookingOption →
                  // legacy fields → segment marketing names → cabin_class) and
                  // hand it down so every SliceCard renders the same brand the
                  // detail modal shows.
                  const tripCabinFareName = getMarketingFareName({
                    flight_data: trip.flight_data,
                    cabin_class: trip.cabin_class,
                  });
                  return trip.flight_data?.slices?.map((slice, si) => (
                    <SliceCard
                      key={si}
                      slice={slice}
                      label={si === 0 ? 'Departing' : 'Return'}
                      cabinFareName={tripCabinFareName}
                    />
                  ));
                })()}

                {/* Current price (tracked when card is expanded; active trips only) */}
                {trip.status === 'active' && (
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 sm:p-4">
                  <p className="text-white/50 text-xs font-normal mb-2">Current base fare for this flight</p>
                  {isLoadingPriceCheck && (
                    <div className="flex items-center gap-2 text-white/40 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                      <span>Checking latest price…</span>
                    </div>
                  )}
                  {priceCheckError && !isLoadingPriceCheck && (
                    <p className="text-brand-400/90 text-sm">{priceCheckError}</p>
                  )}
                  {priceCheck && !isLoadingPriceCheck && (
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="text-white font-normal">
                        {priceCheck.current_price_display ?? '—'}
                      </span>
                      <span className="text-white/40 text-xs">your plan total: {priceCheck.trip_total_display}</span>
                      {priceCheck.matched && priceCheck.current_price_cents != null && (
                        <span className="text-xs">
                          {priceCheck.price_dropped && (
                            <span className="text-emerald-400/90">{"Price dropped — you'd pay less when you book."}</span>
                          )}
                          {priceCheck.price_same && (
                            <span className="text-white/50">Same price.</span>
                          )}
                          {priceCheck.price_increased && (
                            <span className="text-brand-400/80">{"Price went up. Reserve prices aren't locked, so you'd pay the difference when you book."}</span>
                          )}
                        </span>
                      )}
                      {!priceCheck.matched && priceCheck.current_price_cents == null && (
                        <span className="text-white/60 text-xs">Exact flight not in current results; search for alternatives if needed.</span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-white/[0.04]">
                    {planEnded ? (
                      <p className="text-white/60 text-xs font-normal">
                        Your plan ended on {deadlineText}. You can still use your Reserve balance on any flight: choose Reserve as your payment method at checkout.
                      </p>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const fd = trip.flight_data;
                          const s0 = fd?.slices?.[0];
                          const seg0 = s0?.segments?.[0];
                          const lastSeg = s0?.segments?.[s0.segments?.length - 1];
                          const origin = seg0?.origin?.iata_code || trip.departure_city?.slice(0, 3)?.toUpperCase() || '';
                          const dest = lastSeg?.destination?.iata_code || trip.arrival_city?.slice(0, 3)?.toUpperCase() || '';
                          const depDate = seg0?.departing_at ? new Date(seg0.departing_at).toISOString().slice(0, 10) : trip.departure_date?.slice(0, 10) || '';
                          const retSlice = fd?.slices?.[1];
                          const retDate = retSlice?.segments?.[0]?.departing_at ? new Date(retSlice.segments[0].departing_at).toISOString().slice(0, 10) : trip.return_date?.slice(0, 10) || '';
                          const passengers = fd?._searchParams?.passengers || [{ type: 'adult' }];
                          const cabin = trip.cabin_class || 'economy';
                          const params = new URLSearchParams({
                            origin: origin || 'XXX',
                            destination: dest || 'XXX',
                            departureDate: depDate,
                            cabin: cabin || 'economy'
                          });
                          writePassengerParams(params, passengers);
                          if (retDate) params.set('returnDate', retDate);
                          params.set('searchMethod', 'reserve_switch');
                          navigate(`/search?${params.toString()}`, {
                            state: { reserveSwitchTrip: trip }
                          });
                        }}
                        className="w-full sm:w-auto text-white/70 hover:text-white hover:bg-white/5 border border-white/10 rounded-full text-xs h-9"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-2" />
                        Switch Flight
                      </Button>
                    )}
                  </div>
                </div>
                )}

                {/* Payment details grid */}
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2.5 pt-1">
                  <div className="rounded-lg sm:rounded-xl bg-white/[0.025] border border-white/[0.04] p-2 sm:p-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                      <CreditCard className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/20" />
                      <span className="text-white/30 text-xs sm:text-xs">Per payment</span>
                    </div>
                    <p className="text-white/85 text-xs sm:text-base font-normal">${fmtCurrency(trip.payment_amount_cents)}</p>
                  </div>
                  <div className="rounded-lg sm:rounded-xl bg-white/[0.025] border border-white/[0.04] p-2 sm:p-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                      <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/20" />
                      <span className="text-white/30 text-xs sm:text-xs">Frequency</span>
                    </div>
                    <p className="text-white/85 text-xs sm:text-base font-normal">{frequencyLabels[trip.payment_frequency]}</p>
                  </div>
                  <div className="rounded-lg sm:rounded-xl bg-white/[0.025] border border-white/[0.04] p-2 sm:p-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                      <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/20" />
                      <span className="text-white/30 text-xs sm:text-xs">Remaining</span>
                    </div>
                    <p className="text-white/85 text-xs sm:text-base font-normal">{trip.payments_remaining} pmt{trip.payments_remaining !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="rounded-lg sm:rounded-xl bg-white/[0.025] border border-white/[0.04] p-2 sm:p-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                      <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/20" />
                      <span className="text-white/30 text-xs sm:text-xs">Deadline</span>
                    </div>
                    <p className="text-white/85 text-xs sm:text-base font-normal">
                      {deadlineText}
                    </p>
                  </div>
                  {allocatedCents > 0 && trip.status !== 'ticketed' && (
                    <div className="col-span-2 rounded-lg sm:rounded-xl bg-emerald-500/5 border border-emerald-500/10 p-2 sm:p-3">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                        <Wallet className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400/70" />
                        <span className="text-emerald-400/70 text-xs sm:text-xs">Allocated from Reserve</span>
                      </div>
                      <p className="text-emerald-400/90 text-xs sm:text-base font-normal">${fmtCurrency(allocatedCents)}</p>
                    </div>
                  )}
                </div>

                {/* Book Flight - for completed (Ready for Ticketing) trips */}
                {trip.status === 'completed' && (
                  <div className="pt-2">
                    <Button
                      size="sm"
                      onClick={handleBookFlight}
                      disabled={isBooking}
                      className="w-full sm:w-auto bg-white text-black hover:bg-white/90 rounded-full text-sm font-normal px-5 py-2.5"
                    >
                      {isBooking ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Loading…
                        </>
                      ) : (
                        <>
                          <Ticket className="w-4 h-4 mr-2" />
                          Book Flight
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Inject from Reserve / Push back / Schedule change / cancel */}
                {trip.status === 'active' && (
                  <>
                    {showScheduleChange ? (
                      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-4">
                        <p className="text-white/50 text-sm font-normal">Change payment frequency</p>
                        <div className="grid grid-cols-3 gap-2">
                          {['weekly', 'biweekly', 'monthly'].map((f) => (
                            <button
                              key={f}
                              onClick={() => handleFrequencySelect(f)}
                              className={`py-2 px-3 rounded-lg text-xs font-normal transition-all
                                ${newFrequency === f ? 'bg-white text-black' : 'bg-white/[0.03] text-white/50 border border-white/[0.06] hover:border-white/10'}`}
                            >
                              {frequencyLabels[f]}
                            </button>
                          ))}
                        </div>

                        {isLoadingPreview && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 text-white/20 animate-spin" />
                          </div>
                        )}

                        {preview && !isLoadingPreview && (
                          <div className="space-y-3">
                            {preview.paid_amount_cents > 0 && (
                              <div className="flex items-center justify-between rounded-lg bg-white/[0.02] border border-white/[0.04] px-3 py-2">
                                <span className="text-white/25 text-[11px]">Already paid</span>
                                <span className="text-white/40 text-[11px] font-normal">${fmtCurrency(preview.paid_amount_cents)}</span>
                              </div>
                            )}
                            <p className="text-white/20 text-[10px] text-center">Remaining ${fmtCurrency(preview.remaining_cents)} spread across future payments — no charge today</p>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="rounded-lg border border-white/[0.04] bg-white/[0.015] p-3">
                                <p className="text-white/25 text-[10px] uppercase tracking-wider mb-2">Current · {frequencyLabels[preview.current_frequency]}</p>
                                <p className="text-white/50 text-xs font-normal mb-2">
                                  {preview.current_schedule.num_payments} × ${fmtCurrency(preview.current_schedule.per_payment_cents)}
                                </p>
                                <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                                  {preview.current_schedule.dates.map((d, i) => (
                                    <div key={i} className="flex justify-between text-[11px]">
                                      <span className="text-white/20">{format(parseISO(d.date), 'MMM d')}</span>
                                      <span className="text-white/30">${fmtCurrency(d.amount_cents)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
                                <p className="text-emerald-400/50 text-[10px] uppercase tracking-wider mb-2">New · {frequencyLabels[preview.proposed_frequency]}</p>
                                <p className="text-emerald-400/70 text-xs font-normal mb-2">
                                  {preview.proposed_schedule.num_payments} × ${fmtCurrency(preview.proposed_schedule.per_payment_cents)}
                                </p>
                                <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                                  {preview.proposed_schedule.dates.map((d, i) => (
                                    <div key={i} className="flex justify-between text-[11px]">
                                      <span className="text-emerald-400/30">{format(parseISO(d.date), 'MMM d')}</span>
                                      <span className="text-emerald-400/50">${fmtCurrency(d.amount_cents)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 text-xs">
                              <span className="text-white/25">${fmtCurrency(preview.current_schedule.per_payment_cents)} × {preview.current_schedule.num_payments}</span>
                              <ArrowRight className="w-3 h-3 text-white/15" />
                              <span className="text-emerald-400/70">${fmtCurrency(preview.proposed_schedule.per_payment_cents)} × {preview.proposed_schedule.num_payments}</span>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleUpdateSchedule}
                            disabled={isUpdating || newFrequency === trip.payment_frequency}
                            className="flex-1 bg-white text-black hover:bg-white/90 rounded-full text-xs h-9 disabled:opacity-30"
                          >
                            {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm Change'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setShowScheduleChange(false); setNewFrequency(trip.payment_frequency); setPreview(null); }}
                            className="border border-white/[0.06] text-white/40 hover:text-white/60 hover:bg-white/[0.03] rounded-full text-xs h-9"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {showInjectButton && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowInjectModal(true);
                              setInjectAmountCents('');
                              setInjectPreview(null);
                            }}
                            className="border border-emerald-500/20 text-emerald-400/80 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-full text-xs h-9"
                          >
                            <ArrowDownToLine className="w-3 h-3 mr-1.5" />
                            Inject from Reserves
                          </Button>
                        )}
                        {allocatedCents > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowPushBackModal(true);
                              setPushBackAmountCents('');
                            }}
                            className="border border-white/[0.06] text-white/40 hover:text-white/60 hover:bg-white/[0.03] rounded-full text-xs h-9"
                          >
                            <ArrowUpFromLine className="w-3 h-3 mr-1.5" />
                            Push back to Reserve
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleOpenScheduleChange}
                          className="border border-white/[0.06] text-white/40 hover:text-white/60 hover:bg-white/[0.03] rounded-full text-xs h-9"
                        >
                          <RefreshCw className="w-3 h-3 mr-1.5" />
                          Change Schedule
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowCancelConfirm(true)}
                          disabled={isCancelling}
                          className="border border-red-500/10 text-red-400/50 hover:text-red-400/80 hover:bg-red-500/[0.06] rounded-full text-xs h-9"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel Plan
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Cancel confirmation overlay */}
      <AnimatePresence>
        {showCancelConfirm && (
          <CancelConfirmOverlay
            trip={trip}
            onConfirm={handleCancel}
            onClose={() => setShowCancelConfirm(false)}
            isLoading={isCancelling}
          />
        )}
      </AnimatePresence>

      {/* Inject from Reserve modal */}
      <AnimatePresence>
        {showInjectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => {
                if (!isInjecting) {
                  setShowInjectModal(false);
                  setInjectCap(null);
                  setInjectCapError(null);
                }
              }}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-white text-lg font-medium mb-1">Allocate to this trip</h3>
              <p className="text-white/60 text-sm font-normal mb-4">
                Move funds from your Reserve balance to this trip. We’ll recalc your payment schedule (no new charge).
              </p>
              <p className="text-white/60 text-xs mb-2">
                {injectCapError ? (
                  <span className="text-brand-400/90">{injectCapError}</span>
                ) : injectCap == null && showInjectModal ? (
                  'Loading max…'
                ) : (
                  <>You can allocate up to {fmtCurrency(maxInjectCents)} (unallocated balance, capped by this trip's remaining amount).</>
                )}
              </p>
              <div className="mb-4">
                <label className="block text-white/50 text-xs mb-1">Amount to allocate ($)</label>
                <input
                  type="number"
                  min="0"
                  max={maxInjectCents / 100}
                  step="0.01"
                  value={injectAmountCents}
                  onChange={(e) => {
                    setInjectAmountCents(e.target.value);
                    setInjectPreview(null);
                  }}
                  placeholder="0.00"
                  className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                />
                {(() => {
                  const cents = Math.round(parseFloat(injectAmountCents || 0) * 100) || 0;
                  if (cents > maxInjectCents && maxInjectCents >= 0) {
                    return (
                      <p className="text-brand-400/90 text-xs mt-1.5">
                        Maximum for this trip is {fmtCurrency(maxInjectCents)}. Reduce the amount to continue.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              {!injectPreview ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const v = parseFloat(injectAmountCents);
                    if (Number.isNaN(v) || v <= 0) {
                      toast.error('Enter an amount');
                      return;
                    }
                    const cents = Math.round(v * 100);
                    if (cents > maxInjectCents) {
                      toast.error(`Maximum you can allocate is ${fmtCurrency(maxInjectCents)}`);
                      return;
                    }
                    fetchInjectPreview(cents);
                  }}
                  disabled={isLoadingInjectPreview || !injectAmountCents || parseFloat(injectAmountCents) <= 0 || (injectCap == null && !injectCapError)}
                  className="w-full border border-white/20 text-white/70 rounded-xl text-sm h-10 mb-3"
                >
                  {isLoadingInjectPreview ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</> : (injectCap == null && !injectCapError ? 'Loading max…' : 'Preview new schedule')}
                </Button>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    <p className="text-white/60 text-xs">Is this schedule okay? Confirm to update your subscription.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-white/[0.04] bg-white/[0.015] p-3">
                        <p className="text-white/25 text-[10px] uppercase tracking-wider mb-2">Current · {frequencyLabels[trip.payment_frequency] ?? trip.payment_frequency}</p>
                        <p className="text-white/50 text-xs font-normal mb-2">
                          {injectPreview.current_schedule?.num_payments ?? 0} × ${fmtCurrency(injectPreview.current_schedule?.per_payment_cents ?? 0)}
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                          {(injectPreview.current_schedule?.dates ?? []).map((d, i) => (
                            <div key={i} className="flex justify-between text-[11px]">
                              <span className="text-white/20">{format(parseISO(d.date), 'MMM d')}</span>
                              <span className="text-white/30">${fmtCurrency(d.amount_cents)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.03] p-3">
                        <p className="text-emerald-400/50 text-[10px] uppercase tracking-wider mb-2">New · {frequencyLabels[trip.payment_frequency] ?? trip.payment_frequency}</p>
                        <p className="text-emerald-400/70 text-xs font-normal mb-2">
                          {injectPreview.proposed_schedule.num_payments} × ${fmtCurrency(injectPreview.proposed_schedule.per_payment_cents)}
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                          {(injectPreview.proposed_schedule.dates ?? []).map((d, i) => (
                            <div key={i} className="flex justify-between text-[11px]">
                              <span className="text-emerald-400/30">{format(parseISO(d.date), 'MMM d')}</span>
                              <span className="text-emerald-400/50">${fmtCurrency(d.amount_cents)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-3 text-xs">
                      <span className="text-white/25">${fmtCurrency(injectPreview.current_schedule?.per_payment_cents ?? 0)} × {injectPreview.current_schedule?.num_payments ?? 0}</span>
                      <ArrowRight className="w-3 h-3 text-white/15" />
                      <span className="text-emerald-400/70">${fmtCurrency(injectPreview.proposed_schedule.per_payment_cents)} × {injectPreview.proposed_schedule.num_payments}</span>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowInjectModal(false);
                    setInjectAmountCents('');
                    setInjectPreview(null);
                    setInjectCap(null);
                    setInjectCapError(null);
                  }}
                  disabled={isInjecting}
                  className="flex-1 border border-white/10 text-white/60 rounded-full text-xs h-9"
                >
                  Cancel
                </Button>
                {injectPreview ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      const cents = Math.round(parseFloat(injectAmountCents || 0) * 100) || 0;
                      if (cents > maxInjectCents) {
                        toast.error(`Maximum you can allocate is ${fmtCurrency(maxInjectCents)}`);
                        return;
                      }
                      handleInjectConfirm();
                    }}
                    disabled={isInjecting || !injectAmountCents || parseFloat(injectAmountCents) <= 0 || (Math.round(parseFloat(injectAmountCents || 0) * 100) > maxInjectCents)}
                    className="flex-1 bg-emerald-500/90 text-white hover:bg-emerald-500 rounded-full text-xs h-9 disabled:opacity-50"
                  >
                    {isInjecting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Push back to Reserve modal */}
      <AnimatePresence>
        {showPushBackModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !isPushingBack && (setShowPushBackModal(false), setPushBackPreview(null), setPushBackAmountCents(''))} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-white text-lg font-medium mb-1">Push back to Reserve</h3>
              <p className="text-white/60 text-sm font-normal mb-4">
                Return allocated amount to your general Reserve balance. Your payment schedule will be recalculated (no new charge).
              </p>
              {!pushBackPreview ? (
                <>
                  <div className="mb-4">
                    <label className="block text-white/50 text-xs mb-1">Amount ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={allocatedCents / 100}
                      value={pushBackAmountCents}
                      onChange={(e) => { setPushBackAmountCents(e.target.value); setPushBackPreview(null); }}
                      placeholder={`Max $${fmtCurrency(allocatedCents)}`}
                      className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-white/20"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const v = parseFloat(pushBackAmountCents);
                      if (Number.isNaN(v) || v <= 0) { toast.error('Enter an amount'); return; }
                      const cents = Math.round(v * 100);
                      if (cents > allocatedCents) { toast.error(`Maximum is $${fmtCurrency(allocatedCents)}`); return; }
                      fetchPushBackPreview(cents);
                    }}
                    disabled={isLoadingPushBackPreview || !pushBackAmountCents || parseFloat(pushBackAmountCents) <= 0 || parseFloat(pushBackAmountCents) * 100 > allocatedCents}
                    className="w-full border border-white/20 text-white/70 rounded-xl text-sm h-10 mb-3"
                  >
                    {isLoadingPushBackPreview ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</> : 'Preview new schedule'}
                  </Button>
                </>
              ) : (
                <div className="space-y-3 mb-4">
                  <p className="text-white/60 text-xs">Remaining amount due will increase; subscription payments will be recalculated.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-white/[0.04] bg-white/[0.015] p-3">
                      <p className="text-white/25 text-[10px] uppercase tracking-wider mb-2">Current · {frequencyLabels[trip.payment_frequency] ?? trip.payment_frequency}</p>
                      <p className="text-white/50 text-xs font-normal mb-2">
                        {pushBackPreview.current_schedule?.num_payments ?? 0} × ${fmtCurrency(pushBackPreview.current_schedule?.per_payment_cents ?? 0)}
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                        {(pushBackPreview.current_schedule?.dates ?? []).map((d, i) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-white/20">{format(parseISO(d.date), 'MMM d')}</span>
                            <span className="text-white/30">${fmtCurrency(d.amount_cents)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-lg border border-brand-500/15 bg-brand-500/[0.03] p-3">
                      <p className="text-brand-400/50 text-[10px] uppercase tracking-wider mb-2">New · {frequencyLabels[trip.payment_frequency] ?? trip.payment_frequency}</p>
                      <p className="text-brand-400/70 text-xs font-normal mb-2">
                        {pushBackPreview.proposed_schedule?.num_payments ?? 0} × ${fmtCurrency(pushBackPreview.proposed_schedule?.per_payment_cents ?? 0)}
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                        {(pushBackPreview.proposed_schedule?.dates ?? []).map((d, i) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-brand-400/30">{format(parseISO(d.date), 'MMM d')}</span>
                            <span className="text-brand-400/50">${fmtCurrency(d.amount_cents)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-xs">
                    <span className="text-white/25">${fmtCurrency(pushBackPreview.current_schedule?.per_payment_cents ?? 0)} × {pushBackPreview.current_schedule?.num_payments ?? 0}</span>
                    <ArrowRight className="w-3 h-3 text-white/15" />
                    <span className="text-brand-400/70">${fmtCurrency(pushBackPreview.proposed_schedule?.per_payment_cents ?? 0)} × {pushBackPreview.proposed_schedule?.num_payments ?? 0}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (pushBackPreview) setPushBackPreview(null);
                    else { setShowPushBackModal(false); setPushBackAmountCents(''); setPushBackPreview(null); }
                  }}
                  disabled={isPushingBack}
                  className="flex-1 border border-white/10 text-white/60 rounded-full text-xs h-9"
                >
                  {pushBackPreview ? 'Back' : 'Cancel'}
                </Button>
                {pushBackPreview ? (
                  <Button
                    size="sm"
                    onClick={handlePushBackConfirm}
                    disabled={isPushingBack || !pushBackAmountCents || parseFloat(pushBackAmountCents) <= 0 || Math.round(parseFloat(pushBackAmountCents) * 100) > allocatedCents}
                    className="flex-1 bg-white/10 text-white hover:bg-white/20 rounded-full text-xs h-9 disabled:opacity-50"
                  >
                    {isPushingBack ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function ReserveTripsCard({ trips, isLoading, onRefresh, balanceCents = 0, totalAllocatedCents = 0, allocationColumnMissing = false }) {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = React.useState(false);

  if (isLoading) {
    return (
      <div className="rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] overflow-hidden">
        <SectionBand
          icon={PlaneTakeoff}
          iconColor="text-[hsl(228,35%,58%)]"
          accentRgb="10, 18, 51"
          title="Reserved Flights"
        />
        <div className="flex items-center justify-center py-12 p-6 sm:p-8">
          <Loader2 className="w-6 h-6 text-white/30 animate-spin" />
        </div>
      </div>
    );
  }

  const activeTrips = trips.filter((t) => (t.archived_at == null) && t.status !== 'cancelled');
  const archivedTrips = trips.filter((t) => t.archived_at != null || t.status === 'cancelled');

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] overflow-hidden">
      <SectionBand
        icon={PlaneTakeoff}
        iconColor="text-[hsl(228,35%,58%)]"
        accentRgb="10, 18, 51"
        title="Reserved Flights"
        subtitle={
          activeTrips.length > 0 || archivedTrips.length > 0
            ? `${activeTrips.length} trip${activeTrips.length !== 1 ? 's' : ''}${archivedTrips.length > 0 ? ` · ${archivedTrips.length} archived` : ''}`
            : undefined
        }
        rightSlot={
          <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
            {activeTrips.length}
          </span>
        }
      />

      <div className="p-4 sm:p-8">
      {trips.length === 0 ? (
        <div className="text-center py-8">
          <PlaneTakeoff className="w-12 h-12 text-white/[0.06] mx-auto mb-3" />
          <p className="text-white/30 text-sm font-normal">No reserve trips yet</p>
          <p className="text-white/15 text-xs font-normal mt-1">
            Find a flight and add it to Reserve to get started
          </p>
          <Button
            onClick={() => navigate('/search')}
            className="mt-6 bg-white text-black hover:bg-white/90 rounded-full px-6"
          >
            <Search className="w-4 h-4 mr-2" />
            Search flights
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {activeTrips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              allTrips={activeTrips}
              onRefresh={onRefresh}
              balanceCents={balanceCents}
              totalAllocatedCents={totalAllocatedCents}
              allocationColumnMissing={allocationColumnMissing}
            />
          ))}
          {archivedTrips.length > 0 && (
            <div className="pt-2 border-t border-white/[0.06]">
              <button
                type="button"
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 w-full py-2 text-left text-white/40 hover:text-white/60 text-sm font-normal transition-colors"
              >
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
                {showArchived ? 'Hide archived' : `Show ${archivedTrips.length} archived`}
              </button>
              <AnimatePresence>
                {showArchived && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-3 pt-2">
                      {archivedTrips.map((trip) => (
                        <TripCard
                          key={trip.id}
                          trip={trip}
                          allTrips={trips}
                          onRefresh={onRefresh}
                          balanceCents={balanceCents}
                          totalAllocatedCents={totalAllocatedCents}
                          allocationColumnMissing={allocationColumnMissing}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
